/* eslint-disable no-unused-vars, no-undef */
import ky from 'ky';
import { useEffect, useState, useMemo, useRef, type CSSProperties } from 'react';
import { Form, Row, Col, Alert, Button } from 'react-bootstrap';
import { BsQuestionCircle } from 'react-icons/bs';

import BayAvatar from '../../BayAvatar';
import BayModalLoader from '../../BayModalLoader';
import BayTooltip from '../../BayTooltip';
import BayRcDialog from '../../BayRcDialog';
import { ActAsSessionLoader } from './ActAsSessionOverlay';
import BaySelect from '../../BaySelect';
import getProfileImageUrl from './getProfileImageUrl';
import setActingAs from './setActingAs';
import '../styles/actAsModal.scss';
import type { BooleanCallback } from '../../types/callbacks';
import type { TranslationFunction } from '../../types/common';

// The host app's global. This file already calls it in eight places behind
// no-undef disables; declaring it once stops each call being a TS2304 as well.
// Type-only -- nothing about the runtime changes. Narrowed to the two members
// this file actually uses rather than `any`, so the actAsFunc response below is
// still checked.
declare const jQuery: {
	iqUIProcessing(action: string, label?: string): void;
	actAsFunc(data: Record<string, string | undefined>): Promise<{
		status?: string;
		errors?: string[];
	}>;
};

interface AgentData {
	publicid: string;
	firstname?: string;
	lastname?: string;
	first_name?: string;
	last_name?: string;
	name?: string;
	officename?: string;
	group?: string;
	accesslevel?: string;
	type?: string;
	image_data?: string;
	profile?: string;
	label?: string;
	value?: string;
}

interface User {
	PUBLICID?: string;
	ACCESSLEVEL?: string;
	ActingAsPUBLICID?: string;
	API_AUTH_TOKEN?: string;
	ActingAsFIRSTNAME?: string;
	ActingAsLASTNAME?: string;
	ActingAsCOMPANYNAME?: string;
	displayName?: string;
	type?: string;
	ActingAsACCESSLEVEL?: string;
	ActingAsIMAGE_DATA?: string;
	ActingAsIMAGEPATH?: string;
}

interface SocialActAsOptions {
	account_id?: string;
	actor?: string;
	bay_access_token?: string;
}

interface ActAsResponse {
	USERPREFS: string[];
	QUERY: AgentData[];
}

interface SocialActAsResponse {
	arrayOfUsers?: AgentData[];
	hiddenInputs?: SocialActAsOptions;
}

interface ActAsModalProps {
	platform?: string;
	showModal: boolean;
	onHideFunc: BooleanCallback;
	user?: User;
	t?: TranslationFunction;
	/** Cannot be dismissed: no close button, no cancel, no escape/outside press. */
	required?: boolean;
	/** Signs the user out. Required mode's only exit that is not an impersonation. */
	onLogout?: () => void;
}

const ModalLoading = () => {
	return (
		<Row className="justify-content-center">
			<Col
				className="justify-content-center align-content-center text-center"
				md="auto"
			>
				<p>Please wait...</p>
			</Col>
		</Row>
	);
};

const getRoleLabel = (accessLevel = '') => {
	const normalizedAccessLevel = String(accessLevel).toUpperCase();

	if (normalizedAccessLevel === 'A') {
		return 'Admin';
	}

	if (normalizedAccessLevel === 'S') {
		return 'Staff';
	}

	if (normalizedAccessLevel === 'U') {
		return 'User';
	}

	return '';
};

// BaySelect sizes the virtualised list as
// `min(options, MAX_VISIBLE_OPTIONS) * OPTION_HEIGHT`. The six-row cap is the Figma's
// annotation; OPTION_HEIGHT is 7 + 42 + 7 and tracks the option's own `py-2` (step 2 of
// the spacing scale, 0.5rem = 7px at the 14px root) and `.act-as-agent-identity
// .avatar-md` in actAsModal.scss -- change either, change this.
const OPTION_HEIGHT = 57;
const MAX_VISIBLE_OPTIONS = 6;

// One reader for the shapes this modal is handed: ClientBAY's agents.cfc rows
// (firstname/officename), SocialBAY's /site/get-act-as-data rows (first_name, or a
// pre-built name/label), and the normalised "currently acting as" object. Deriving
// these per-template is how the option row and the pinned header used to disagree
// about which fields to fall back to.
const getAgentDisplay = (agent: Partial<AgentData> = {}) => {
	const firstName = agent.firstname || agent.first_name || '';
	const lastName = agent.lastname || agent.last_name || '';

	return {
		fullName: `${firstName} ${lastName}`.trim() || agent.name || agent.label || '',
		role: getRoleLabel(agent.accesslevel) || agent.type || '',
		location: agent.officename || agent.group || '',
	};
};

// Avatar + name + "{type} | {group}" -- one component for the pinned box, every
// option and the closed control, because the design draws one row.
const AgentIdentity = ({ agent }: { agent: Partial<AgentData> }) => {
	const { fullName, role, location } = getAgentDisplay(agent);

	return (
		<div className="act-as-agent-identity d-flex align-items-center gap-2">
			<BayAvatar imgUrl={agent.profile} size="md" />
			<div className="act-as-agent-identity-content d-flex flex-column gap-1">
				{fullName && (
					<div className="act-as-agent-name text-900 text-truncate">
						{fullName}
					</div>
				)}
				{(role || location) && (
					<div className="act-as-agent-meta d-flex align-items-center gap-2 fs-7 text-500">
						{role && <span className="text-truncate">{role}</span>}
						{/* Decorative, and only drawn when there are two values to separate. */}
						{role && location && (
							<span aria-hidden="true" className="act-as-agent-meta-divider" />
						)}
						{location && <span className="text-truncate">{location}</span>}
					</div>
				)}
			</div>
		</div>
	);
};

// BaySelect invokes these as plain functions, not as JSX components. The option and
// the selected value render the same row, so they share one renderer.
const renderAgentIdentity = (agent: AgentData) => <AgentIdentity agent={agent} />;

// Required mode removes every exit, and its backdrop covers the nav -- so the
// account menu's Logout goes with them. Without this the only way out of the gate
// is to start an Act As session on someone, which is an audit-logged
// impersonation the admin may not have intended (signing into the wrong tenant is
// the case that bites). Sign out is the one exit that is not an impersonation.
const RequiredGateSignOut = ({
	t,
	onLogout,
}: {
	t: TranslationFunction;
	onLogout: () => void;
}) => {
	const signingOut = useRef(false);

	const handleSignOut = () => {
		// iqLogOff navigates rather than resolving, so the window between the click
		// and the page leaving is wide enough to click in again.
		if (signingOut.current) {
			return;
		}

		signingOut.current = true;

		try {
			onLogout();
		} catch (error) {
			signingOut.current = false;

			throw error;
		}
	};

	return (
		<Button
			variant="link"
			size="sm"
			className="align-self-start px-0"
			onClick={handleSignOut}
		>
			{t('signOutInstead')}
		</Button>
	);
};

// Defined here rather than in the render body: a component created inside another
// component's body is a new type every render, so React remounts the real node.
const RetryActAsList = ({
	t,
	onRetry,
}: {
	t: TranslationFunction;
	onRetry: () => void;
}) => (
	<Button variant="outline-primary" size="sm" className="mt-3 align-self-start" onClick={onRetry}>
		{t('retryLoadingAccounts')}
	</Button>
);

const ActAsModal = ({
	platform,
	showModal,
	onHideFunc,
	user,
	t = (key) => key,
	required = false,
	onLogout,
}: ActAsModalProps) => {
	const [selectedOption, setSelectedOption] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [agentList, setAgentList] = useState<AgentData[]>([]);
	const [socialActAsOptions, setSocialActAsOptions] = useState<SocialActAsOptions>({});
	const [errorState, setErrorState] = useState<string | null>(null);
	const [actAsLoading, setActAsLoading] = useState(false);
	// Bumped by the retry control so the fetch effect re-runs. Without it a failed
	// agent-list fetch was terminal: the deps only change when the modal reopens,
	// and in `required` mode it never closes.
	const [reloadToken, setReloadToken] = useState(0);
	// Per-run, not module-scoped. A shared controller stayed aborted after the
	// first cleanup, so every later fetch started on a dead signal and the list
	// was empty for the rest of the page's life.
	const controllerRef = useRef<AbortController | null>(null);
	// Don't add an `isKeyboardNav` state back. One used to drive the hover-versus-
	// keyboard highlight from a class on the select's wrapper, but the menu is
	// portalled, so a pointer crossing it never reached that wrapper and the flag stuck
	// on after the first arrow key -- the "no hover on the user rows" bug. CSS decides
	// it now; see `.act-as-dropdown-menu` in ../styles/actAsModal.scss.

	const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 575;

	// The menu opens upward, so an oversized one runs off the TOP of the window rather
	// than politely off the bottom. Track the viewport so a short window can shrink it.
	const [viewportHeight, setViewportHeight] = useState(
		typeof window !== 'undefined' ? window.innerHeight : 0,
	);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return undefined;
		}

		const handleViewportResize = () => setViewportHeight(window.innerHeight);

		window.addEventListener('resize', handleViewportResize);

		return () => window.removeEventListener('resize', handleViewportResize);
	}, []);

	// A ceiling, not a height: shorter content still renders at its own height, which
	// is what stops a single search result sitting inside a scroll area.
	const maxMenuHeight = Math.max(
		OPTION_HEIGHT * 3,
		Math.round(viewportHeight * 0.6),
	);

	const mobileBodyStyle: CSSProperties = isMobileView ? {
		padding: 0,
		height: 'calc(100vh - 9.15rem)',
		overflowY: 'auto',
	} : {
		padding: 0,
	};

	const isClient = platform === 'client' || platform === 'social';

	// The dialog body is `overflow-y: auto`, and a scroll container clips on BOTH axes,
	// so the menu was cut off at its edge. A clip cannot be fixed with z-index -- the
	// menu has to leave the element. `menuPosition: fixed` is required alongside the
	// portal, or the menu positions against the document and detaches on any scroll.
	const menuPortalTarget =
		typeof document !== 'undefined' ? document.body : undefined;
	// The only two rules that cannot be SCSS: react-select writes both elements'
	// styles INLINE, which no selector outranks.
	const actAsMenuStyles = useMemo(
		() => ({
			menuPortal: (base) => ({
				...base,
				// Clears --bs-zindex-modal (1055). react-select's own default is 1,
				// which would put the menu behind the dialog it belongs to.
				// eslint-disable-next-line no-restricted-syntax -- grandfathered: raw z-index fenced as step 2b, the portal band is a cross-repo decision. See docs/css-unification/overlay-and-zindex.md
				zIndex: 1080,
			}),
			// react-select's default `4px 0`, dropped so the list sits flush inside the
			// menu's own padding and the MAX_VISIBLE_OPTIONS maths is not 8px out.
			menuList: (base) => ({
				...base,
				padding: 0,
			}),
		}),
		[],
	);

	// These classes are what let the portalled menu be styled from SCSS at all, and
	// BaySelect is what makes them land: react-select only reads `classNames` from
	// 5.5.0, and smc resolves bay-ui's imports against its own pinned 5.4.0, which
	// drops it silently. See `withClassName` in ../../BaySelect/index.tsx.
	//
	// `p-3`/`rounded-1`/`px-3` are the design's 14px and 3.5px on this theme's 14px
	// root. Utilities compile `!important` here, so they clear react-select's emotion.
	// Before moving anything else onto a utility, check it HAS `!important` -- the
	// option's `cursor: pointer` cannot move for exactly that reason (see the SCSS).
	const actAsSelectClassNames = useMemo(
		() => ({
			menu: () => 'act-as-dropdown-menu p-3 rounded-1',
			option: () => 'act-as-dropdown-option px-3 py-2',
		}),
		[],
	);

	// Social's rows already carry label/value/profile; every other platform's arrive as
	// raw agent records. This mapping was gated on `platform === 'client'`, so the
	// legacy platform's options reached react-select with no `value` at all. The label
	// comes from `getAgentDisplay` so the text searched is the text displayed.
	const agentOptions = useMemo(() => {
		return agentList.map((agent) => {
			const { fullName, location } = getAgentDisplay(agent);
			const optionData: AgentData = {
				...agent,
				profile: getProfileImageUrl(agent.image_data, agent.profile || ''),
				label: location ? `${fullName} - ${location}` : fullName,
				value: agent.value || `${agent.publicid}`,
			};

			return optionData;
		});
	}, [agentList, platform]);

	const selectedAgentName = useMemo(() => {
		if (!selectedOption) {
			return '';
		}

		const selectedAgent = agentOptions.find((agent) => {
			const optionValue = String(agent?.value ?? agent?.publicid ?? '');
			return optionValue === String(selectedOption);
		});

		if (!selectedAgent) {
			return '';
		}

		return getAgentDisplay(selectedAgent).fullName;
	}, [agentOptions, selectedOption]);

	const isActingAsAnotherUser = useMemo(() => {
		const actingAsPublicId = String(user?.ActingAsPUBLICID ?? '');
		const userPublicId = String(user?.PUBLICID ?? '');

		return actingAsPublicId.length > 0 && actingAsPublicId !== userPublicId;
	}, [user]);

	const currentlyActingAsData = useMemo(() => {
		if (!isActingAsAnotherUser) {
			return null;
		}

		const firstName = user?.ActingAsFIRSTNAME || '';
		const lastName = user?.ActingAsLASTNAME || '';
		const officeName = user?.ActingAsCOMPANYNAME || user?.displayName || '';
		const rawType = user?.type ? String(user.type) : '';
		const type = rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : '';

		return {
			firstname: firstName,
			lastname: lastName,
			officename: officeName,
			accesslevel: user?.ActingAsACCESSLEVEL || '',
			type,
			profile: getProfileImageUrl(
				user?.ActingAsIMAGE_DATA,
				user?.ActingAsIMAGEPATH || '',
			),
		};
	}, [isActingAsAnotherUser, user]);

	// NO SCROLL LOCK HERE, and none needed. Do not add one back.
	//
	// A hand-rolled one used to live here that snapshotted `document.body.style.overflow`
	// and wrote it back on close. SNAPSHOT AND RESTORE is the failure this whole overlay
	// effort exists to remove (.plan/feat-overlay-consolidation/overview.md, failure 1):
	// whoever snapshots second captures another locker's lock as the "original" and
	// re-applies it permanently on close.
	//
	// It was also redundant and uncompensated, because rc-dialog was locking `body`
	// underneath it -- gated on its lock flag, not on whether it portalled, so even
	// `getContainer={false}` did not switch it off. That lock is what made the fixed top
	// nav jump sideways by the scrollbar width on every open: rc-dialog compensated by
	// narrowing `body`, and a `position: fixed` nav sizes to the VIEWPORT, not to body.
	//
	// Phase 2 moved this dialog onto BayFloatManager, which locks nothing it is not told
	// to and never restores a snapshot. `scrollbar-gutter: stable` on each app's scroller
	// retires the shift independently.

	useEffect(() => {
		const controller = new AbortController();
		const { signal } = controller;
		setErrorState(null);

		if (showModal && user) {
			controllerRef.current = controller;
		}

		async function fetch() {
			if (showModal && user) {
				const formData = new FormData();
				formData.append('method', 'getActingAs');
				formData.append('returnformat', 'json');
				formData.append('layout', '');
				formData.append('Return_Query', true);

				try {
					if (platform !== 'client') {
						jQuery.iqUIProcessing('open', 'Processing');
					}
					setLoading(true);
					const res = await ky
						.post('/global/components/agents.cfc', {
							body: formData,
							signal,
							retry: 2,
							timeout: 30000,
						})
						.json<ActAsResponse>();

					// TODO: Migrate agent filtering from ClientBAY,
					// and into the query itself
					const userPrefs = res.USERPREFS;
					let agentsArray: AgentData[] = Array.isArray(res.QUERY) ? res.QUERY : [];
					const userId = user?.PUBLICID;
					const accessLevel = user?.ACCESSLEVEL;
					const publicId = user?.ActingAsPUBLICID;

					const isInPrefArray = function (id: string): boolean {
						if (userPrefs.length) {
							return !!userPrefs.find((e) => e === id);
						} else {
							return false;
						}
					};

					// Filter out self
					agentsArray = agentsArray.filter((e) => e.publicid !== publicId);

					const filterFunc = (accessLevel: string | undefined, agentData: AgentData): boolean => {
						if (accessLevel === 'A') {
							// Admin, make sure I can't act as other admins unless specified
							if (
								agentData.accesslevel === 'A' &&
								!isInPrefArray(agentData.publicid)
							) {
								return false;
							} else {
								return true;
							}
						} else if (accessLevel === 'S') {
							// Staff, make sure I can't act as other admins or staff unless specified
							if (
								(agentData.accesslevel === 'A' ||
									agentData.accesslevel === 'S') &&
								!isInPrefArray(agentData.publicid)
							) {
								return false;
							} else {
								return true;
							}
						} else {
							// User, make sure I can't act as anyone unless specified
							if (!isInPrefArray(agentData.publicid)) {
								return false;
							} else {
								return true;
							}
						}
					};

					// I am not SU, filter the agents array...
					if (userId && userId.toLowerCase() !== 'realistiq') {
						agentsArray = agentsArray.filter((e) =>
							filterFunc(accessLevel, e),
						);
					}
					// If I am SU, show all agents
					setAgentList(agentsArray);
					setLoading(false);
					/* eslint-disable-next-line no-undef */
					if (platform !== 'client') {
						jQuery.iqUIProcessing('close');
					}
				} catch (error) {
					// Checked BEFORE any state write: by the time an aborted request
					// rejects, a newer effect run owns `loading` and has its own
					// request in flight. Clearing loading here dropped gateBlocked's
					// guard mid-fetch.
					if ((error as Error).name === 'AbortError') {
						console.log('ActAs Fetch aborted by user');

						// The overlay is the exception, because the `open` above is
						// unpaired otherwise. An abort is either a re-run -- the ref
						// already points at the newer controller, whose own overlay
						// must stay up -- or a close/unmount, where nothing else will
						// ever close it and it blocks the whole page.
						/* eslint-disable-next-line no-undef */
						if (controllerRef.current === controller && platform !== 'client') {
							jQuery.iqUIProcessing('close');
						}

						return;
					}

					setLoading(false);
					/* eslint-disable-next-line no-undef */
					if (platform !== 'client') {
						jQuery.iqUIProcessing('close');
					}
					console.error('ActAs Fetch error:', error);
					// Has to reach the user, not just the console: in `required`
					// mode an empty list with no error reads as "nobody to act
					// as" and leaves no way forward.
					setErrorState(t('actAsListLoadFailed'));
				}
			}
		}

		async function fetchSocial() {
			// Fetch our agents list
			if (showModal && user) {
				try {
					setLoading(true);
					const res = await ky
						.get('/site/get-act-as-data', {
							headers: {
								Authorization: `${user.API_AUTH_TOKEN}`,
								'X-Requested-With': 'XMLHttpRequest',
							},
							signal,
							retry: 2,
							timeout: 30000,
						})
						.json<SocialActAsResponse>();

					if (res?.arrayOfUsers?.length && res.arrayOfUsers.length > 0) {
						setAgentList(res.arrayOfUsers);
						setSocialActAsOptions(res.hiddenInputs || {});
					}

					setLoading(false);
				} catch (error) {
					// Same ordering as fetch() above: an aborted run leaves no trace,
					// because a newer run already owns this state.
					if ((error as Error).name === 'AbortError') {
						console.log('Getting Act As List action aborted by user');

						return;
					}

					setLoading(false);
					console.error('Getting Act As List action error:', error);
					setErrorState(t('actAsListLoadFailed'));
				}
			}
		}

		if (platform === 'social') {
			fetchSocial();
		} else {
			// TODO: Some day refactor this re-use the ActAs.cfc
			fetch();
		}

		return () => {
			controller.abort();
			setSelectedOption(null);
		};
	}, [showModal, user, platform, reloadToken]);

	// `required` removes the close button, the cancel button, escape and backdrop
	// dismissal, and submit is disabled on an empty list -- so a list that cannot
	// be loaded left a global admin on a dead screen with the backdrop covering
	// the nav's Logout too. A gate that cannot be satisfied does not get to trap
	// the user: once the fetch has settled with nothing to pick, the exits come
	// back and a retry is offered.
	const gateBlocked = required && !loading && agentList.length < 1;

	const retryFetch = () => {
		setErrorState(null);
		setReloadToken((token) => token + 1);
	};

	const hideAndCancel = () => {
		if (required && !gateBlocked) {
			return;
		}

		controllerRef.current?.abort();

		setSelectedOption(null);
		onHideFunc(true);
	};

	// TODO: Refactor away this jQuery stuff. Some day...
	const actAsFunc = async () => {
		if (agentList.length < 1) {
			return;
		}

		if (platform !== 'social') {
			try {
				/* eslint-disable-next-line no-undef */
				if (platform !== 'client') {
					jQuery.iqUIProcessing('open', 'Processing');
				}
				setLoading(true);
				setActAsLoading(true);

				if (!selectedOption) return;
				const { ok, message } = await setActingAs(selectedOption);

				if (!ok) {
					/* eslint-disable-next-line no-undef */
					if (platform !== 'client') {
						jQuery.iqUIProcessing('close');
					}
					setLoading(false);
					setActAsLoading(false);
					setErrorState(message || t('actAsFailed'));
					return;
				}

				localStorage.removeItem('CurrentActingAsROLE');

				window.location.replace('/');
			} catch (error) {
				/* eslint-disable-next-line no-undef */
				if (platform !== 'client') {
					jQuery.iqUIProcessing('close');
				}
				setLoading(false);
				setActAsLoading(false);
				setErrorState(`Error Setting Act As user failed. Details: ${error}`);
			}
		} else {
			setErrorState(null);
			setLoading(true);
			setActAsLoading(true);
			try {
				const formDataObj = {
					account_id: socialActAsOptions.account_id,
					actor: socialActAsOptions.actor,
					bay_access_token: socialActAsOptions.bay_access_token,
					imp_account_id: selectedOption,
				};

				/* eslint-disable-next-line no-undef */
				const res = await jQuery.actAsFunc(formDataObj);

				if (res?.status === 'failed') {
					setErrorState(res.errors[0]);
					setLoading(false);
					setActAsLoading(false);
				}
			} catch (error) {
				setLoading(false);
				setActAsLoading(false);
				if ((error as Error).name === 'AbortError') {
					console.log('Posting Act As action aborted by user');
				} else {
					console.error('Error Posting Act As action. Details:', error);
				}
			}
		}
	};

	// The pinned box above the option list. `px-3` is deliberately the same utility the
	// option rows carry -- it is what lines this avatar and name up with the list below,
	// so widening it indents the pinned row out of that column.
	const currentlyActingAsHeader = currentlyActingAsData ? (
		<div className="d-flex flex-column gap-1 mb-2 py-4 px-3 border rounded-1 bg-50">
			<div className="text-600">{t('currentlyActingAs')}</div>
			<AgentIdentity agent={currentlyActingAsData} />
		</div>
	) : null;

	// Needs BaySelect's non-array branch in `CustomMenuList` to render at all -- see
	// the note there.
	const noOptionsMessage = () => (
		<div className="act-as-no-results p-3 text-center">
			<div className="text-900">{t('noResultsFound')}</div>
			<div className="fs-7 text-500">{t('noResultText')}</div>
		</div>
	);

	// One configuration for both hosts, which had drifted apart. Only the placeholder
	// copy differs between them.
	const sharedSelectProps = {
		classNames: actAsSelectClassNames,
		customSingleValueTemplate: renderAgentIdentity,
		customStyles: actAsMenuStyles,
		customTemplate: renderAgentIdentity,
		isClearable: false,
		maxMenuHeight,
		maxVisibleOptions: MAX_VISIBLE_OPTIONS,
		menuListHeader: currentlyActingAsHeader,
		// Always above the control, per the design. `top` is a fixed placement, not
		// react-select's `auto`, so the menu never flips down over the footer buttons.
		menuPlacement: 'top' as const,
		menuPortalTarget,
		menuPosition: 'fixed' as const,
		menuShouldScrollIntoView: false,
		// menuPosition is fixed, so the menu is placed against the viewport once and
		// does not track a scrolling ancestor. Required mode gives the column its own
		// scroller, so without this the open menu stays parked while the control
		// slides away under it.
		//
		// A predicate, not `true`: react-select's boolean branch only closes when the
		// scroll target passes isDocumentElement (documentElement/body/window), and
		// the column here is a plain div -- so `true` would close on nothing at all.
		// The menu's own virtualised list is excluded, or scrolling the options would
		// dismiss them.
		closeMenuOnScroll: (event: Event) =>
			event.target instanceof Element &&
			!event.target.closest('.bay-select__menu'),
		NoOptionsMessage: noOptionsMessage,
		noOptionMessage: true,
		optionHeight: OPTION_HEIGHT,
		optionsArray: agentOptions,
		useVirtualScroll: true,
	};

	return (
		<>
			{/*
			 * `getContainer={false}` is gone, and with it the `ActAsModalStylesWrapper`
			 * that forced it. That wrapper's only content was descendant selectors for
			 * this dialog's chrome, so the dialog had to render inside it; the rules are
			 * in ../styles/actAsModal.scss now and the dialog portals like every other
			 * overlay. See .plan/feat-overlay-consolidation/phase-2.md.
			 *
			 * The centring utilities went with it. `top-50 start-50 translate-middle`
			 * positioned the panel by hand because rc-dialog rendered it in the nav's
			 * flow; the engine's backdrop is a flex container that centres it, and a
			 * leftover `translate-middle` would fight the panel's own enter transform.
			 * `vw-100 mw-100 vh-100` is `size="fullscreen"` — which also drops the
			 * radius and the centring gutter, which those three could not do.
			 */}
			<BayRcDialog
				className={isClient ? `actAsModalMobileDialog ${actAsLoading ? 'actAsModalSessionPending' : loading ? 'actAsModalMobileDialogLoading p-4 rounded-2' : 'actAsModalMobileDialogReady'}` : ''}
				closeButton={(required && !gateBlocked) || (isClient && loading) ? false : true}
				hideCancel={required && !gateBlocked}
				buttonText={isClient ? t('startActAsSession') : 'Ok'}
				disabled={loading || agentList.length < 1 || selectedOption === null}
				mask
				show={showModal}
				submitFunc={() => actAsFunc()}
				title={isClient && loading ? undefined : (isClient ? t('actAsAnotherUser') : 'Act As')}
				onHideFunc={hideAndCancel}
				showHeaderBorder={isClient}
				bodyStyle={isClient && !loading ? mobileBodyStyle : undefined}
				fullscreen={isClient && isMobileView}
				size={isClient && !loading ? 'lg' : isMobileView ? 'sm' : undefined}
				hideFooter={isClient && loading ? true : false}
				bodyProps={isClient && loading ? { className: 'rounded-2' } : undefined}
				maskClassName={isClient && actAsLoading ? 'actAsModalLoadingMask' : undefined}
				/* `required` joins the client case: at the default backdrop (1050) the
				   account drawer's panel (1055) stays lit and clickable beside a dialog
				   that claims to be undismissable, which puts two live logout controls
				   on screen at once -- the thing each surface's in-flight guard assumes
				   cannot happen. */
				zIndexBackdrop={
					platform === 'client' || required
						? 'var(--bs-zindex-modal, 1055)'
						: undefined
				}
			>
				{loading && !isClient ? (
					<ModalLoading />
				) : (
					<>
						{!isClient ? (
							<div className="d-flex flex-column" style={{ lineHeight: '1.5rem' }}>
								{required ? (
									<Alert variant="info" className="mb-4">
										<Alert.Heading as="h5">{t('actAsRequiredForGlobalAdminTitle')}</Alert.Heading>
										<p className="mb-0">{t('actAsRequiredForGlobalAdmin')}</p>
									</Alert>
								) : (
									<p> {t('chooseAccountToActAs')} </p>
								)}
								{errorState && <Alert variant="warning">{errorState}</Alert>}
								<div className="d-flex flex-column">
									{agentList.length >= 1 ? (
										<Form onSubmit={actAsFunc}>
											<Form.Group className="my-1" controlId="actAs-select">
												{/* Same wrapper and shared props as the client variant below. */}
												<div className="act-as-select">
													<BaySelect
														{...sharedSelectProps}
														placeholder={t('chooseAccount')}
														onChangeFunc={(e) => setSelectedOption(e.value)}
													/>
												</div>
											</Form.Group>
											<Form.Text muted>{t('typeToSearchForAnAccount')}</Form.Text>
										</Form>
									) : (
										<>
											<Alert variant="info" className="mb-0">
												{t('noAdditionalUsersAlertMessage')}
											</Alert>
											{gateBlocked && (
												<RetryActAsList t={t} onRetry={retryFetch} />
											)}
										</>
									)}
									{/* After the picker deliberately: the gate hides close and
										cancel, so anything rendered above the select becomes the
										dialog's first tab stop -- and a keyboard user would meet
										the escape hatch before the action the gate exists for. */}
									{required && onLogout && (
										<RequiredGateSignOut t={t} onLogout={onLogout} />
									)}
								</div>
							</div>
						) : isClient && loading && !actAsLoading ? (
							<BayModalLoader
								show
								variant="content"
								spinnerSize={28}
								t={t}
								loadingText={t('preparingUserDirectory')}
							/>
						) : isClient && actAsLoading ? (
							<ActAsSessionLoader
								label={t('startingSecureActAsSession')}
								name={selectedAgentName || 'user_name'}
								t={t}
							/>
						) : (
							<div className={`d-sm-flex align-items-center ${isMobileView ? 'h-100' : 'modalBodyHeight'}`}>
								{/* `h-100` and `overflow-hidden` are load-bearing, not decoration. The
									    parent is `align-items-center`, so this column's `align-self` is
									    `center` and its height is CONTENT-based -- which left the img's own
									    `h-100` resolving against an indefinite height, i.e. `auto`. The image
									    rendered at its natural aspect ratio, taller than the 396px row, and
									    spilled out of the panel. A definite height gives the img something to
									    resolve against so `object-fit: cover` crops instead of overflowing. */}
								<div className={isMobileView ? 'w-100' : 'w-50 h-100 overflow-hidden'}>
									<img
										src="https://s3.us-west-1.wasabisys.com/bay/client/act_as_user_selection_image.png"
										alt={t('actAsModalContent')}
										className={`img-fluid w-100 object-fit-cover ${isMobileView ? 'modalImageHeight' : 'h-100'}`}
									/>
								</div>
								{/* px-5 py-4, not p-5. 3rem top and bottom put the panel over the
									    viewport on a short window, so the body scrolled -- and the body is
									    `overflow-y: auto`, which is the second scrollbar. 1.5rem vertical
									    gives back 48px and the sides keep their 3rem. */}
								{/* The required column scrolls itself. The body deliberately does
									    not (`overflow: clip` in actAsModal.scss, so a few pixels over
									    never shows a second scrollbar), and the row is capped at a
									    fixed height -- so whatever is last in this column is what gets
									    clipped. Under `required` that is the sign-out, the only exit
									    from the gate that is not an impersonation, and the error and
									    empty-list states stack above it. A local scroller keeps it
									    reachable without touching the body rule. */}
								<div
									className={`${isMobileView ? 'w-100 p-3' : 'w-50 px-5 py-4'}${
										required && !isMobileView ? ' align-self-stretch overflow-y-auto' : ''
									}`}
								>
									{/* Required mode is the global admin's only way in, so the notice
										    replaces the pitch and the warning rather than stacking on top of
										    them -- all three together overflowed the panel and scrolled the
										    select out of sight. */}
									{required ? (
										<Alert variant="info" className="mb-4">
											<Alert.Heading as="h5">{t('actAsRequiredForGlobalAdminTitle')}</Alert.Heading>
											<p className="mb-0">{t('actAsRequiredForGlobalAdmin')}</p>
										</Alert>
									) : (
										<>
											<p className="mb-4">{t('actAsAnotherUserDescription')}</p>
											<p className="mb-4 bg-yellow-100 border border-yellow-200 rounded-1 p-3">{t('actAsAnotherUserWarning')}</p>
										</>
									)}
									<div className="d-flex align-items-center gap-1 mb-1">
										{/* Figma: Desktop/Body/Semi-bold - Inter 600, 14px,
											   150% line-height, 0% letter-spacing, bs-dark. */}
										<span
											className="fw-semibold text-dark"
										>
											{t('actAsUser')}
										</span>
										{/*
											  * Was `alignConfig={userTooltipAlign}` --
											  * `points: ['bc', 'tc']` is exactly `placement="top"`
											  * (popup bottom-centre to target top-centre, so the
											  * arrow is centred on the icon), and `offset: [0, 3]`
											  * moved it 3px closer. The default gap is 4, hence 1.
											  */}
										<BayTooltip
											arrowOffset={1}
											overlay={t('actAsAnotherUserTooltipText')}
											overlayMaxWidth={320}
											placement="top"
										>
											<span className="cursor-pointer d-inline-flex align-items-center">
												<BsQuestionCircle color="var(--bs-gray-700)" size={14} />
											</span>
										</BayTooltip>
									</div>
									<div className="d-flex flex-column">
										{/* The client variant had no error surface at all -- errorState
										    was only ever rendered in the branch above, so on this
										    platform a refused Act As or a failed list load said
										    nothing. */}
										{errorState && (
											<Alert variant="warning">{errorState}</Alert>
										)}
										{agentList.length >= 1 ? (
											<Form onSubmit={actAsFunc}>
												<Form.Group className="my-1" controlId="actAs-select">
													{/* Wrapper rules are `.act-as-select` in ../styles/actAsModal.scss. */}
													<div className="act-as-select">
														<BaySelect
															{...sharedSelectProps}
															placeholder={t('searchHereOrSelect')}
															onChangeFunc={(e) => setSelectedOption(e.value)}
														/>
													</div>
												</Form.Group>
											</Form>
										) : (
											<>
												<Alert variant="info" className="mb-0">
													{t('noAdditionalUsersAlertMessage')}
												</Alert>
												{gateBlocked && (
													<RetryActAsList t={t} onRetry={retryFetch} />
												)}
											</>
										)}
										{/* After the picker -- see the note in the sibling branch. */}
										{required && onLogout && (
											<RequiredGateSignOut t={t} onLogout={onLogout} />
										)}
									</div>
								</div>
							</div>
						)}
					</>
				)}
			</BayRcDialog>
		</>
	);
};

export default ActAsModal;
